import { FC, useMemo } from 'react';
import ErrorText from '../../Text/ErrorText/ErrorText';
import styles from './Radio.module.scss';
import { withStyles } from '../../../utils';
import { useDatadogLog } from '../logging';

type OnChangeProps = {
  id: string;
  value: string;
};

const classes = withStyles(styles);

export type RadioProps = {
  varient?: 'regular' | 'inline' | 'outlineShadow'; // TODO: deprecate
  variant?: 'regular' | 'inline' | 'outlineShadow'; // properly spelled  :)
  name: string;
  options: { [key: string]: string | React.JSX.Element };
  reverse?: boolean;
  noOrdering?: boolean;
  value: string;
  width?: string;
  readOnly?: boolean;
  disabled?: boolean;
  error?: string | null;
  style?: {
    '--radioItemBorderRadius'?: string;
    '--radioContainerBoxShadow'?: string;
  };

  disableOptionWhen?: (value: string) => boolean;
  setCheckedValue?: (value: string) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, props: OnChangeProps) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>, props: OnChangeProps) => void;
};

const Radio: FC<RadioProps> = ({
  varient,
  variant,
  name,
  options,
  reverse = false,
  noOrdering = false,
  value,
  width = '16px',
  readOnly = false,
  disabled = false,
  error,
  style,
  setCheckedValue = () => {},
  onChange = () => {},
  onBlur = () => {},
  onFocus = () => {},
  disableOptionWhen = () => false,
}) => {
  const hasError = useMemo(() => error !== '' && error !== undefined && error !== null, [error]);

  const log = useDatadogLog();
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    log({
      message: `${event.currentTarget.value} selected for ${name}`,
      payload: {
        name,
        value: event.currentTarget.value,
      },
      actionType: 'Option',
    });
    onChange(event, { id: name, value: event.currentTarget.value });
    setCheckedValue(event.target.value);
  };

  return (
    <>
      <div className={classes(`variant-${variant || varient || 'regular'}`)} style={style}>
        {getOrderedOptions().map(([optionValue, label], idx) => (
          <label
            key={`option-${idx}`}
            htmlFor={`${name}${idx}`}
            className={classes('radioContainer', hasError && 'error', disableOptionWhen(optionValue) && 'disabled')}
          >
            <input
              id={`${name}${idx}`}
              name={name}
              style={{ width, height: width }}
              value={optionValue}
              type="radio"
              checked={optionValue === value}
              disabled={disabled || readOnly}
              onChange={handleChange}
              onFocus={(e) => {
                onFocus(e);
              }}
              onBlur={(e) => {
                onBlur(e, { id: name, value: e.currentTarget.value });
              }}
              data-testid={`radio-${name}${idx}`}
            />
            <span className={classes(readOnly && 'readOnly')}>{label}</span>
          </label>
        ))}
      </div>
      <ErrorText error={error} size="sm" indentation />
    </>
  );

  function getOrderedOptions() {
    if (noOrdering) {
      return Object.entries(options);
    }
    const list = Object.entries(options).sort();
    return reverse ? list.reverse() : list;
  }
};

export default Radio;
